import {AfterViewInit, Component, Input, OnInit, ViewChild} from '@angular/core';
import {MatSort, Sort} from "@angular/material/sort";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatTableDataSource} from "@angular/material/table";
import {Router} from "@angular/router";

// service
import {AuthService} from "../../../shared/services/auth.service";
import {TasksService} from "../../../shared/services/tasks.service";

// models
import {Task} from "../../../shared/models/tasks/task";
import {Tasks} from "../../../shared/models/tasks/tasks";
import {TaskStatusModalComponent} from "../task-status-modal/task-status-modal.component";
import {TaskCompletedModalComponent} from "../task-completed-modal/task-completed-modal.component";
import {MatDialog} from "@angular/material/dialog";

// date picker
import {FormControl} from "@angular/forms";
import {MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS} from '@angular/material-moment-adapter';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import _moment from 'moment';
import {MatSelectChange} from "@angular/material/select";

const moment = _moment;

export const DATE_FORMAT = {
    parse: {
        dateInput: ['YYYY-MM-DD']
    },
    display: {
        dateInput: 'DD/MM/Y',
        monthYearLabel: 'DD MM Y',
        dateA11yLabel: 'DD MM Y',
        monthYearA11yLabel: 'DD MM Y',
    },
};


@Component({
    selector: 'app-tasks-list',
    templateUrl: './tasks-list.component.html',
    styleUrls: ['./tasks-list.component.scss', './../../secure.component.scss'],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: DATE_FORMAT},
    ],
})
export class TasksListComponent implements OnInit, AfterViewInit {

    @Input() public isArchive = 'false';

    @ViewChild(MatSort) public sort!: MatSort;
    @ViewChild(MatPaginator) public paginator!: MatPaginator;

    public isLoading = false;
    public displayedColumns: string[] = ['created_at', 'deadline', 'priority', 'name', 'status', 'actions'];
    public tasks$ = new MatTableDataSource<Task>();
    public pageSizeOptions: number[] = [6, 12, 24, 48];
    public totalRows = 0;
    public pageSize = 6;
    public currentPage = 0;
    public tasksSort = 'created_at';
    public tasksDirection = 'asc';

    // filters
    isDeadline = false;
    public date = new FormControl<_moment.Moment | null>(moment());
    public dateFrom: string | undefined = '';
    public dateTo: string | undefined = '';
    public deadline = new FormControl<_moment.Moment | null>(moment());
    public deadlineFrom: string | undefined = '';
    public deadlineTo: string | undefined = '';
    public priorityFilter = '';
    public statusFilter = '';
    public completed = 'completed';
    public searchFilter = '';

    constructor(protected authService: AuthService,
                private router: Router,
                private tasksService: TasksService,
                private dialog: MatDialog) {
    }

    ngAfterViewInit(): void {
        this.tasks$.paginator = this.paginator;
        this.paginator.firstPage();
    }

    ngOnInit(): void {
        this.loadTasks();
    }

    public pageChanged(event: PageEvent): void {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex + 1;
        this.loadTasks();
    }

    public sortData(sort: Sort): void {
        switch (sort.active) {
            case 'created_at':
                this.tasksSort = sort.active;
                this.tasksDirection = sort.direction;
                this.loadTasks();
                return
            case 'deadline':
                this.tasksSort = sort.active;
                this.tasksDirection = sort.direction;
                this.loadTasks();
                return
            case 'priority':
                this.tasksSort = sort.active;
                this.tasksDirection = sort.direction;
                this.loadTasks();
                return
            case 'name':
                this.tasksSort = sort.active;
                this.tasksDirection = sort.direction;
                this.loadTasks();
                return
            case 'status':
                this.tasksSort = sort.active;
                this.tasksDirection = sort.direction;
                this.loadTasks();
                return
            default:
                return
        }
    }

    public addTask(): void {
        this.router.navigateByUrl(`secure/task-add`).then();
    }

    public showTask(id: number): void {
        this.router.navigateByUrl(`secure/task-show/${id}`).then();
    }

    public setSearchFilter(): void {
        this.searchFilter.trim().toLowerCase();
        this.loadTasks();
        this.paginator.firstPage();
    }

    public changeTaskStatus(taskID: number, taskPriority: string, taskStatus: string): void {
        this.dialog.open(TaskStatusModalComponent, {
            data: {
                taskID: taskID,
                taskPriority: taskPriority,
                taskStatus: taskStatus
            }
        }).afterClosed().subscribe(
            (res): void => {
                if (!res) {
                    this.router.navigateByUrl(`/secure/tasks`).then();
                    this.loadTasks();
                }
            }
        );
    }

    public completeTask(taskID: number): void {
        this.dialog.open(TaskCompletedModalComponent, {
            data: {
                taskID: taskID
            }
        }).afterClosed().subscribe(
            (res): void => {
                if (!res) {
                    this.router.navigateByUrl(`/secure/tasks`).then();
                    this.loadTasks();
                }
            }
        );
    }

    public cleanFilter(): void {
        this.priorityFilter = '';
        this.statusFilter = '';
        this.searchFilter = '';
        this.dateFrom = '';
        this.dateTo = '';
        this.deadlineFrom = '';
        this.deadlineTo = '';
        this.date.setValue(moment());
        this.deadline.setValue(moment());
        this.loadTasks();
        this.paginator.firstPage();
    }

    public setSearchFromDate(): void {
        this.dateFrom = this.date.getRawValue()?.format('YYYY-MM-DD').toString();
    }

    public setSearchToDate(): void {
        this.dateTo = this.date.getRawValue()?.format('YYYY-MM-DD').toString();
    }

    public setSearchFromDeadlineDate(): void {
        this.deadlineFrom = this.deadline.getRawValue()?.format('YYYY-MM-DD').toString();
    }

    public setSearchToDeadlineDate(): void {
        this.deadlineTo = this.deadline.getRawValue()?.format('YYYY-MM-DD').toString();
    }

    public searchByDate(): void {
        this.loadTasks();
        this.paginator.firstPage();
    }

    public setPriorityFilter(option: MatSelectChange): void {
        this.priorityFilter = option.value;
        this.loadTasks();
        this.paginator.firstPage();
    }

    public setStatusFilter(option: MatSelectChange): void {
        this.statusFilter = option.value;
        this.loadTasks();
        this.paginator.firstPage();
    }

    private loadTasks(): void {
        this.isLoading = true;
        const userType = this.authService.user.type;
        this.tasksService.index(
            userType,
            this.currentPage,
            this.pageSize,
            this.tasksSort,
            this.tasksDirection,
            this.searchFilter,
            this.isArchive,
            this.priorityFilter,
            this.statusFilter,
            this.dateFrom,
            this.dateTo,
            this.deadlineFrom,
            this.deadlineTo
        ).subscribe((tasks: Tasks): void => {
                this.tasks$ = new MatTableDataSource(tasks.data);
                this.tasks$.sort = this.sort;
                this.paginator.length = tasks.meta.total;
                this.paginator.pageIndex = 0;
                this.paginator.pageIndex = tasks.meta.current_page - 1;
                this.isLoading = false;
            }
        );
    }
}
